import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  HostListener,
  OnDestroy,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RoleService } from '@app/shared/services/roles.service';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzGridComponent } from '@template-ui/components/ez-grid/ez-grid.component';
import { ButtonsCellRenderer } from '@template-ui/components/ez-grid/cell-renderers/buttons-cell-renderer/buttons-cell-renderer.component';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import type { ColDef, GridOptions } from 'ag-grid-community';
import type { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { WorkflowDraftConfirmationService } from '../workflow-registry-diagnostics/workflow-draft-confirmation.service';
import { AlertTemplateDraftStateService } from './alert-template-draft-state.service';
import type {
  AlertTemplateContextProfile,
  AlertTemplateDetail,
  AlertTemplatePage,
  AlertTemplatePreview,
  AlertTemplateSelectionAction,
  AlertTemplateSummary,
} from './platform-alert-templates.models';
import { PlatformAlertTemplatesService } from './platform-alert-templates.service';

const emptyPage = (): AlertTemplatePage => ({
  page: 1,
  pageSize: 20,
  totalCount: 0,
  canCreate: false,
  createScopes: [],
  items: [],
});
const isTemplateCellActivation = (event: KeyboardEvent): boolean =>
  ['Enter', ' '].includes(event.key) &&
  !(
    event.target instanceof Element &&
    event.target.closest('button,a,input,select,textarea,[contenteditable]')
  );

@Component({
  selector: 'phms-platform-alert-templates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzGridComponent,
    EzStandardStateComponent,
  ],
  templateUrl: './platform-alert-templates.component.html',
  styleUrl: './platform-alert-templates.component.scss',
})
export class PlatformAlertTemplatesComponent implements OnDestroy {
  private readonly api = inject(PlatformAlertTemplatesService);
  private readonly roles = inject(RoleService);
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  readonly draftState = inject(AlertTemplateDraftStateService);
  readonly canRead = this.roles.hasAnyPermissionSignal('content_template.read');
  catalog = emptyPage();
  detail: AlertTemplateDetail | null = null;
  selectedDetailId: number | null = null;
  search = '';
  page = 1;
  loading = false;
  detailLoading = false;
  accessLost = false;
  stale = false;
  catalogError = '';
  error = '';
  notice = '';
  titleTemplate = '';
  bodyTemplate = '';
  contextProfiles: AlertTemplateContextProfile[] = [];
  profilesLoading = false;
  profilesError = '';
  draftProfileKey = '';
  draftProfileHash = '';
  selectedFieldCode = '';
  reason = '';
  showCreate = false;
  newCode = '';
  newName = '';
  createScope = '';
  selectedVersionId = '';
  draftPreview: AlertTemplatePreview | null = null;
  versionPreview: AlertTemplatePreview | null = null;
  previewBusy = false;
  versionPreviewBusy = false;
  private catalogRequest?: Subscription;
  private detailRequest?: Subscription;
  private mutationRequest?: Subscription;
  private previewRequest?: Subscription;
  private versionRequest?: Subscription;
  private profilesRequest?: Subscription;
  private catalogGeneration = 0;
  private detailGeneration = 0;
  private previewGeneration = 0;
  private versionGeneration = 0;
  private profilesGeneration = 0;
  private destroyed = false;
  private mutationIntent: { fingerprint: string; key: string } | null = null;

  readonly columns: ColDef<AlertTemplateSummary>[] = [
    { field: 'name', headerName: 'Template', flex: 1, minWidth: 190 },
    { field: 'code', headerName: 'Code', minWidth: 160 },
    {
      field: 'scopeKey',
      headerName: 'Scope',
      minWidth: 160,
      valueGetter: ({ data }) =>
        data
          ? `${data.scopeTypeCode}${data.scopeKey ? ' · ' + data.scopeKey : ''}`
          : '',
    },
    { field: 'draftRevision', headerName: 'Draft revision', minWidth: 110 },
    {
      field: 'activeVersionId',
      headerName: 'Active selection',
      minWidth: 130,
      valueFormatter: ({ value }) =>
        value == null ? 'Not selected' : 'Published version selected',
    },
    {
      headerName: '',
      minWidth: 90,
      cellRenderer: ButtonsCellRenderer,
      cellRendererParams: {
        buttonsConfig: [{ id: 'open', buttonText: 'Open' }],
        onButtonClick: ({ rowData }: { rowData: AlertTemplateSummary }) =>
          void this.open(rowData.id),
      },
    },
  ];
  readonly gridOptions: GridOptions<AlertTemplateSummary> = {
    getRowId: ({ data }) => String(data.id),
    onRowClicked: ({ data }) => {
      if (data) void this.open(data.id);
    },
    onCellKeyDown: ({ data, event }) => {
      if (
        !data ||
        !(event instanceof KeyboardEvent) ||
        event.defaultPrevented ||
        event.repeat ||
        !isTemplateCellActivation(event)
      )
        return;
      // Native controls own their keyboard activation; handling their bubbled event here
      // would open twice. Grid cells themselves provide the row's keyboard entry point.
      event.preventDefault();
      void this.open(data.id);
    },
    domLayout: 'autoHeight',
  };

  constructor() {
    // AG Grid handles Space before emitting cellKeyDown. Reserve activation on these
    // read-only cells so the row command receives it; leave all native controls alone.
    for (const column of this.columns)
      column.suppressKeyboardEvent = ({ event }) =>
        isTemplateCellActivation(event);
    effect(() => {
      const allowed = this.canRead();
      untracked(() => {
        if (allowed) {
          this.loadCatalog();
          this.loadContextProfiles();
        } else this.clearProtected();
      });
    });
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearProtected();
  }
  @HostListener('window:beforeunload', ['$event'])
  protectEdits(event: BeforeUnloadEvent): void {
    if (this.draftState.isNavigationBlocked()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  get dirty(): boolean {
    return (
      !!this.detail &&
      (this.titleTemplate !== this.detail.draft.titleTemplate ||
        this.bodyTemplate !== this.detail.draft.bodyTemplate ||
        this.draftProfileKey !== this.profileKey(this.detail.draft) ||
        this.draftProfileHash !== this.detail.draft.profileHash)
    );
  }
  get busy(): boolean {
    return this.draftState.busy();
  }
  get canEdit(): boolean {
    return (
      !!this.detail?.permissions.canAuthor &&
      !this.accessLost &&
      !this.busy &&
      !this.detailLoading
    );
  }
  get canSave(): boolean {
    return this.canEdit && this.dirty && !this.stale && !this.validationError;
  }
  get validationError(): string {
    if (!this.titleTemplate.trim() || !this.bodyTemplate.trim())
      return 'Enter a title and message body.';
    if (this.titleTemplate.length > 200 || this.bodyTemplate.length > 8000)
      return 'Keep the title within 200 characters and the message within 8,000 characters.';
    const profile = this.selectedProfile;
    if (!profile)
      return 'Choose an available context and exact version. Reload the context catalog if needed.';
    if (profile.hash !== this.draftProfileHash)
      return 'This context version has changed. Reload the catalog and explicitly choose a compatible context before saving.';
    const text = this.titleTemplate + '\n' + this.bodyTemplate;
    const tokens = Array.from(text.matchAll(/\{\{([^{}]*)\}\}/g));
    if (!profile.fields.length && /[{}]/.test(text))
      return 'This template supports plain text. Data variables are not available for this context.';
    if (
      tokens.some((match) => !/^[A-Za-z][A-Za-z0-9_.]*$/.test(match[1])) ||
      /[{}]/.test(text.replace(/\{\{([^{}]*)\}\}/g, ''))
    )
      return 'Use a supported {{field.code}} token. Expressions, spaces within tokens and other braces are unavailable.';
    if (
      tokens.some(
        (match) =>
          !profile.fields.some(
            (field) =>
              field.code === match[1] &&
              field.referenceKind === 'business_data',
          ),
      )
    )
      return 'A data field is unavailable for this context. Insert a listed business data field; terminology references are unavailable.';
    return '';
  }
  profileKey(
    profile:
      | { code: string; version: number }
      | { profileCode: string; profileVersion: number },
  ): string {
    return 'code' in profile
      ? JSON.stringify([profile.code, profile.version])
      : JSON.stringify([profile.profileCode, profile.profileVersion]);
  }
  get selectedProfile(): AlertTemplateContextProfile | undefined {
    const matches = this.contextProfiles.filter(
      (profile) => this.profileKey(profile) === this.draftProfileKey,
    );
    return matches.length === 1 ? matches[0] : undefined;
  }
  get businessFields() {
    return (
      this.selectedProfile?.fields.filter(
        (field) => field.referenceKind === 'business_data',
      ) ?? []
    );
  }
  profileChanged(): void {
    this.draftProfileHash = this.selectedProfile?.hash ?? '';
    this.selectedFieldCode = '';
    this.inputChanged();
  }
  insertField(field: 'title' | 'body', input: HTMLTextAreaElement): void {
    if (
      !this.canEdit ||
      !this.businessFields.some(
        (entry) => entry.code === this.selectedFieldCode,
      )
    )
      return;
    const text = field === 'title' ? this.titleTemplate : this.bodyTemplate;
    const token = `{{${this.selectedFieldCode}}}`;
    const start = Math.min(input.selectionStart ?? text.length, text.length);
    const end = Math.min(input.selectionEnd ?? start, text.length);
    const next = text.slice(0, start) + token + text.slice(end);
    if (field === 'title') this.titleTemplate = next;
    else this.bodyTemplate = next;
    input.value = next;
    this.inputChanged();
    input.focus();
    input.setSelectionRange(start + token.length, start + token.length);
  }
  contextEntries(preview: AlertTemplatePreview) {
    return Object.entries(preview.contextEvidence?.values ?? {});
  }
  get currentReview() {
    const draft = this.detail?.draft;
    return draft
      ? this.detail!.reviews.find(
          (review) =>
            review.draftRevision === draft.revision &&
            review.contentHash === draft.contentHash,
        )
      : undefined;
  }
  get canPreview(): boolean {
    return (
      !!this.detail &&
      !this.dirty &&
      !this.busy &&
      !this.stale &&
      !this.detailLoading &&
      !this.previewBusy
    );
  }
  get canReview(): boolean {
    return (
      this.canPreview &&
      !!this.detail?.permissions.canReview &&
      this.previewMatches
    );
  }
  get canPublish(): boolean {
    return (
      this.canPreview &&
      !!this.detail?.permissions.canPublish &&
      !!this.currentReview
    );
  }
  get previewMatches(): boolean {
    return (
      !!this.draftPreview &&
      this.draftPreview.draftRevision === this.detail?.draft.revision &&
      this.draftPreview.contentHash === this.detail.draft.contentHash &&
      this.matchesPreviewProfile(this.draftPreview, this.detail.draft)
    );
  }
  get selectedVersion() {
    return this.detail?.versions.find(
      (version) => String(version.id) === this.selectedVersionId,
    );
  }
  get activeVersion() {
    return this.detail?.versions.find(
      (version) => version.id === this.detail?.activeVersionId,
    );
  }
  get selectionReady(): boolean {
    return (
      !!this.detail &&
      !this.dirty &&
      !this.stale &&
      !this.busy &&
      !this.detailLoading &&
      !!this.reason.trim()
    );
  }
  get canActivate(): boolean {
    return (
      this.selectionReady &&
      !!this.detail?.permissions.canActivate &&
      !!this.selectedVersion &&
      !this.selectedVersion.isRetired &&
      this.selectedVersion.id !== this.detail.activeVersionId
    );
  }
  get canRetire(): boolean {
    return (
      this.selectionReady &&
      !!this.detail?.permissions.canRetire &&
      !!this.selectedVersion &&
      !this.selectedVersion.isRetired
    );
  }
  get canRollback(): boolean {
    return (
      this.selectionReady &&
      !!this.detail?.permissions.canRollback &&
      (this.selectedVersion
        ? !this.selectedVersion.isRetired &&
          this.selectedVersion.id !== this.detail.activeVersionId &&
          this.detail.history.some(
            (entry) =>
              entry.versionId === this.selectedVersion?.id &&
              (entry.action === 'activated' || entry.action === 'rolled_back'),
          )
        : this.detail?.activeVersionId != null)
    );
  }

  inputChanged(): void {
    this.invalidateDraftPreview();
    this.error = '';
    this.notice = '';
    this.updateDirty();
  }
  createChanged(): void {
    this.error = '';
    this.updateDirty();
  }
  scopeIdentity(scope: AlertTemplatePage['createScopes'][number]): string {
    return JSON.stringify([scope.scopeTypeCode, scope.scopeKey]);
  }
  reasonChanged(): void {
    this.updateDirty();
  }
  private updateDirty(): void {
    this.draftState.dirty.set(
      this.dirty ||
        !!this.reason.trim() ||
        (this.showCreate && !!(this.newCode || this.newName)),
    );
  }
  private cancelReads(): void {
    ++this.catalogGeneration;
    ++this.detailGeneration;
    ++this.profilesGeneration;
    this.catalogRequest?.unsubscribe();
    this.detailRequest?.unsubscribe();
    this.profilesRequest?.unsubscribe();
    this.invalidateDraftPreview();
    this.invalidateVersionPreview();
  }
  private clearProtected(): void {
    this.cancelReads();
    this.mutationRequest?.unsubscribe();
    this.catalog = emptyPage();
    this.detail = null;
    this.selectedDetailId = null;
    this.titleTemplate = '';
    this.bodyTemplate = '';
    this.draftProfileKey = '';
    this.draftProfileHash = '';
    this.selectedFieldCode = '';
    this.contextProfiles = [];
    this.profilesLoading = false;
    this.profilesError = '';
    this.reason = '';
    this.newCode = '';
    this.newName = '';
    this.createScope = '';
    this.showCreate = false;
    this.selectedVersionId = '';
    this.loading = false;
    this.detailLoading = false;
    this.stale = false;
    this.error = '';
    this.catalogError = '';
    this.notice = '';
    this.mutationIntent = null;
    this.draftState.dirty.set(false);
    this.draftState.busy.set(false);
  }
  loadCatalog(): void {
    if (!this.canRead() || this.busy || this.destroyed) return;
    this.catalogRequest?.unsubscribe();
    const generation = ++this.catalogGeneration;
    this.loading = true;
    this.catalogError = '';
    this.catalogRequest = this.api
      .list(this.page, this.search.trim())
      .subscribe({
        next: (catalog) => {
          if (generation !== this.catalogGeneration || !this.canRead()) return;
          this.catalog = catalog;
          this.loading = false;
          this.accessLost = false;
        },
        error: (error) => {
          if (generation !== this.catalogGeneration) return;
          this.loading = false;
          if (!this.handleAccess(error))
            this.catalogError =
              'Alert templates could not be loaded. Try again.';
        },
      });
  }
  loadContextProfiles(): void {
    if (!this.canRead() || this.busy || this.destroyed) return;
    this.profilesRequest?.unsubscribe();
    const generation = ++this.profilesGeneration;
    this.profilesLoading = true;
    this.profilesError = '';
    this.profilesRequest = this.api.contextProfiles().subscribe({
      next: (profiles) => {
        if (generation !== this.profilesGeneration || !this.canRead()) return;
        this.contextProfiles = profiles;
        this.profilesLoading = false;
      },
      error: (error) => {
        if (generation !== this.profilesGeneration) return;
        this.profilesLoading = false;
        if (!this.handleAccess(error))
          this.profilesError =
            'The context catalog could not be loaded. Your draft is preserved.';
      },
    });
  }
  checkAccess(): void {
    if (this.busy || this.destroyed) return;
    this.roles.reload();
    if (this.canRead()) {
      this.loadCatalog();
      this.loadContextProfiles();
    }
  }
  filter(): void {
    if (!this.busy) {
      this.page = 1;
      this.loadCatalog();
    }
  }
  changePage(delta: number): void {
    if (this.busy || this.loading) return;
    const next = this.page + delta;
    if (
      next < 1 ||
      (next - 1) * this.catalog.pageSize >= this.catalog.totalCount
    )
      return;
    this.page = next;
    this.loadCatalog();
  }
  async open(id: number): Promise<void> {
    if (this.busy || (id === this.detail?.id && !this.showCreate)) return;
    if (
      !(await this.draftState.confirmNavigation()) ||
      !this.canRead() ||
      this.destroyed
    )
      return;
    this.showCreate = false;
    this.newCode = '';
    this.newName = '';
    this.reason = '';
    this.readDetail(id, false);
  }
  private readDetail(id: number, preserveText: boolean): void {
    if (!this.canRead() || this.busy || this.destroyed) return;
    this.detailRequest?.unsubscribe();
    const generation = ++this.detailGeneration;
    this.selectedDetailId = id;
    this.invalidateDraftPreview();
    this.invalidateVersionPreview();
    this.detailLoading = true;
    this.error = '';
    this.notice = '';
    if (!preserveText) {
      this.detail = null;
      this.titleTemplate = '';
      this.bodyTemplate = '';
      this.draftProfileKey = '';
      this.draftProfileHash = '';
      this.selectedFieldCode = '';
      this.selectedVersionId = '';
      this.updateDirty();
    }
    this.detailRequest = this.api.detail(id).subscribe({
      next: (detail) => {
        if (generation !== this.detailGeneration || !this.canRead()) return;
        this.detailLoading = false;
        if (!detail.permissions.canRead) {
          this.accessLost = true;
          this.clearProtected();
          return;
        }
        this.applyDetail(detail, preserveText);
        if (preserveText)
          this.notice =
            'Source refreshed. Your unsaved text is preserved; review it before saving.';
      },
      error: (error) => {
        if (generation !== this.detailGeneration) return;
        this.detailLoading = false;
        if (!this.handleAccess(error))
          this.error = 'This template could not be loaded. Try again.';
      },
    });
  }
  refreshSource(): void {
    if (this.detail) this.readDetail(this.detail.id, true);
  }
  retryDetail(): void {
    if (this.selectedDetailId !== null)
      this.readDetail(this.selectedDetailId, false);
  }
  async beginCreate(): Promise<void> {
    if (
      !this.catalog.canCreate ||
      this.busy ||
      !(await this.draftState.confirmNavigation()) ||
      !this.canRead() ||
      this.destroyed
    )
      return;
    ++this.detailGeneration;
    this.detailRequest?.unsubscribe();
    this.detailLoading = false;
    this.detail = null;
    this.selectedDetailId = null;
    this.titleTemplate = '';
    this.bodyTemplate = '';
    this.draftProfileKey = '';
    this.draftProfileHash = '';
    this.selectedFieldCode = '';
    this.reason = '';
    this.invalidateDraftPreview();
    this.invalidateVersionPreview();
    this.showCreate = true;
    this.newCode = '';
    this.newName = '';
    this.createScope = '';
    this.error = '';
    this.notice = '';
    this.stale = false;
    this.updateDirty();
  }
  create(): void {
    const scope = this.catalog.createScopes.find(
      (value) => this.scopeIdentity(value) === this.createScope,
    );
    if (
      !this.showCreate ||
      !this.catalog.canCreate ||
      this.createScope === '' ||
      !scope ||
      !this.newCode.trim() ||
      !this.newName.trim()
    )
      return;
    const request = {
      code: this.newCode.trim(),
      name: this.newName.trim(),
      scopeTypeCode: scope.scopeTypeCode,
      scopeKey: scope.scopeKey,
    };
    this.mutate(
      'create',
      request,
      (key) => this.api.create(request, key),
      'Draft created. Preview and independent review are separate actions.',
    );
  }
  async discard(): Promise<void> {
    if (
      this.busy ||
      !(await this.draftState.confirmNavigation()) ||
      this.destroyed
    )
      return;
    this.reason = '';
    this.showCreate = false;
    this.newCode = '';
    this.newName = '';
    if (this.detail) {
      this.titleTemplate = this.detail.draft.titleTemplate;
      this.bodyTemplate = this.detail.draft.bodyTemplate;
      this.draftProfileKey = this.profileKey(this.detail.draft);
      this.draftProfileHash = this.detail.draft.profileHash;
      this.selectedFieldCode = '';
    }
    this.invalidateDraftPreview();
    this.error = '';
    this.notice = '';
    this.updateDirty();
  }
  save(): void {
    if (!this.canSave || !this.detail || !this.selectedProfile) return;
    const detail = this.detail,
      title = this.titleTemplate,
      body = this.bodyTemplate,
      profile = this.selectedProfile;
    this.mutate(
      'save',
      {
        id: detail.id,
        revision: detail.draft.revision,
        title,
        body,
        profileCode: profile.code,
        profileVersion: profile.version,
        profileHash: profile.hash,
      },
      (key) =>
        this.api.save(detail.id, detail.draft, title, body, key, profile),
      'Draft saved. Preview the saved revision before independent review.',
    );
  }
  preview(): void {
    if (!this.canPreview || !this.detail) return;
    const detail = this.detail,
      generation = ++this.previewGeneration;
    this.previewRequest?.unsubscribe();
    this.draftPreview = null;
    this.previewBusy = true;
    this.error = '';
    this.previewRequest = this.api.preview(detail.id, detail.draft).subscribe({
      next: (preview) => {
        if (
          !this.sameSource(detail) ||
          generation !== this.previewGeneration ||
          this.dirty
        )
          return;
        this.previewBusy = false;
        if (
          preview.draftRevision !== detail.draft.revision ||
          preview.contentHash !== detail.draft.contentHash ||
          preview.versionId !== null ||
          !this.matchesPreviewProfile(preview, detail.draft)
        ) {
          this.stale = true;
          this.error =
            'The preview does not match this saved revision. Refresh the source.';
          return;
        }
        this.draftPreview = preview;
      },
      error: (error) => {
        if (generation === this.previewGeneration) {
          this.previewBusy = false;
          this.operationError(error);
        }
      },
    });
  }
  review(): void {
    if (!this.canReview || !this.detail) return;
    const detail = this.detail;
    this.mutate(
      'review',
      { id: detail.id, ...detail.draft },
      (key) => this.api.review(detail.id, detail.draft, key),
      'Independent review recorded for this saved revision. Publication remains separate.',
    );
  }
  async publish(): Promise<void> {
    if (!this.canPublish || !this.detail || !this.currentReview) return;
    const detail = this.detail,
      reviewId = this.currentReview.id;
    const accepted = await this.confirmation.confirm({
      heading: 'Publish alert template?',
      message: `Publish saved draft revision ${detail.draft.revision} of ${detail.name}? Active selection remains a separate action.`,
      confirmText: 'Publish revision',
      cancelText: 'Keep draft',
      tone: 'info',
    });
    if (
      !accepted ||
      !this.canPublish ||
      !this.sameSource(detail) ||
      this.destroyed
    )
      return;
    this.mutate(
      'publish',
      {
        id: detail.id,
        revision: detail.draft.revision,
        hash: detail.draft.contentHash,
        reviewId,
      },
      (key) => this.api.publish(detail.id, detail.draft, reviewId, key),
      'Immutable version published. It is not selected automatically.',
    );
  }
  chooseVersion(): void {
    this.invalidateVersionPreview();
    this.error = '';
  }
  previewVersion(): void {
    if (
      !this.detail ||
      !this.selectedVersion ||
      this.versionPreviewBusy ||
      this.busy
    )
      return;
    const detail = this.detail,
      version = this.selectedVersion,
      generation = ++this.versionGeneration;
    this.versionRequest?.unsubscribe();
    this.versionPreview = null;
    this.versionPreviewBusy = true;
    this.error = '';
    this.versionRequest = this.api
      .previewVersion(detail.id, version.id)
      .subscribe({
        next: (preview) => {
          if (
            generation !== this.versionGeneration ||
            this.detail?.id !== detail.id ||
            this.selectedVersion?.id !== version.id ||
            !this.canRead()
          )
            return;
          this.versionPreviewBusy = false;
          if (
            preview.versionId !== version.id ||
            preview.contentHash !== version.contentHash ||
            !this.matchesPreviewProfile(preview, version)
          ) {
            this.error =
              'The retained preview does not match the selected version.';
            return;
          }
          this.versionPreview = preview;
        },
        error: (error) => {
          if (generation === this.versionGeneration) {
            this.versionPreviewBusy = false;
            this.operationError(error);
          }
        },
      });
  }
  async select(action: AlertTemplateSelectionAction): Promise<void> {
    if (
      !(action === 'activate'
        ? this.canActivate
        : action === 'retire'
          ? this.canRetire
          : this.canRollback) ||
      !this.detail
    )
      return;
    const detail = this.detail,
      version = this.selectedVersion,
      reason = this.reason.trim();
    const actionLabel =
      action === 'activate'
        ? 'Activate'
        : action === 'retire'
          ? 'Retire'
          : 'Roll back selection';
    const accepted = await this.confirmation.confirm({
      heading: `${actionLabel} alert template?`,
      message: `${actionLabel}${version ? ' version ' + version.versionNumber : ' to no active version'} for ${detail.name}? Reason: ${reason}`,
      confirmText: actionLabel,
      cancelText: 'Keep selection',
    });
    if (
      !accepted ||
      !this.sameSource(detail) ||
      this.reason.trim() !== reason ||
      this.selectedVersion?.id !== version?.id ||
      this.busy ||
      this.destroyed
    )
      return;
    if (
      !(action === 'activate'
        ? this.canActivate
        : action === 'retire'
          ? this.canRetire
          : this.canRollback)
    )
      return;
    this.mutate(
      action,
      {
        id: detail.id,
        versionId: version?.id ?? null,
        selectionRevision: detail.selectionRevision,
        reason,
      },
      (key) =>
        this.api.select(
          detail.id,
          action,
          version?.id ?? null,
          detail.selectionRevision,
          reason,
          key,
        ),
      action === 'retire'
        ? 'Version retired. Its retained content and history remain readable.'
        : 'Active selection updated. Published content is unchanged.',
    );
  }
  private sameSource(detail: AlertTemplateDetail): boolean {
    return (
      this.canRead() &&
      !this.accessLost &&
      this.detail?.id === detail.id &&
      this.detail.draft.revision === detail.draft.revision &&
      this.detail.draft.contentHash === detail.draft.contentHash &&
      this.detail.selectionRevision === detail.selectionRevision
    );
  }
  private matchesPreviewProfile(
    preview: AlertTemplatePreview,
    source: {
      profileCode: string;
      profileVersion: number;
      profileHash: string;
    },
  ): boolean {
    const evidence = preview.contextEvidence;
    return (
      preview.isSynthetic === true &&
      preview.profileCode === source.profileCode &&
      preview.profileVersion === source.profileVersion &&
      preview.profileHash === source.profileHash &&
      (source.profileCode === 'assignment_alert_literal'
        ? evidence == null
        : !!evidence) &&
      (!evidence ||
        (evidence.isSynthetic === true &&
          evidence.profileCode === source.profileCode &&
          evidence.profileVersion === source.profileVersion &&
          evidence.profileHash === source.profileHash))
    );
  }
  private applyDetail(detail: AlertTemplateDetail, preserveText = false): void {
    this.detail = detail;
    this.detailLoading = false;
    if (!preserveText) {
      this.titleTemplate = detail.draft.titleTemplate;
      this.bodyTemplate = detail.draft.bodyTemplate;
      this.draftProfileKey = this.profileKey(detail.draft);
      this.draftProfileHash = detail.draft.profileHash;
      this.selectedFieldCode = '';
    }
    if (
      !detail.versions.some(
        (version) => String(version.id) === this.selectedVersionId,
      )
    )
      this.selectedVersionId = '';
    this.accessLost = false;
    this.stale = false;
    this.showCreate = false;
    this.newCode = '';
    this.newName = '';
    this.updateDirty();
  }
  private mutate(
    action: string,
    intent: unknown,
    operation: (key: string) => Observable<AlertTemplateDetail>,
    notice: string,
  ): void {
    if (this.busy || !this.canRead() || this.accessLost || this.destroyed)
      return;
    const fingerprint = JSON.stringify({ action, intent });
    if (this.mutationIntent?.fingerprint !== fingerprint)
      this.mutationIntent = { fingerprint, key: crypto.randomUUID() };
    const generation = this.detailGeneration;
    this.draftState.busy.set(true);
    this.error = '';
    this.notice = '';
    if (action !== 'review') this.invalidateDraftPreview();
    this.invalidateVersionPreview();
    this.mutationRequest = operation(this.mutationIntent.key).subscribe({
      next: (detail) => {
        if (
          generation !== this.detailGeneration ||
          !this.canRead() ||
          this.destroyed
        )
          return;
        this.draftState.busy.set(false);
        if (!detail.permissions.canRead) {
          this.accessLost = true;
          this.clearProtected();
          return;
        }
        this.reason = '';
        this.applyDetail(detail);
        this.notice = notice;
        this.mutationIntent = null;
        this.loadCatalog();
      },
      error: (error) => {
        if (generation !== this.detailGeneration || this.destroyed) return;
        this.draftState.busy.set(false);
        this.operationError(error);
        this.updateDirty();
      },
    });
  }
  private invalidateDraftPreview(): void {
    ++this.previewGeneration;
    this.previewRequest?.unsubscribe();
    this.previewBusy = false;
    this.draftPreview = null;
  }
  private invalidateVersionPreview(): void {
    ++this.versionGeneration;
    this.versionRequest?.unsubscribe();
    this.versionPreviewBusy = false;
    this.versionPreview = null;
  }
  private handleAccess(error: unknown): boolean {
    if (
      !(error instanceof HttpErrorResponse) ||
      (error.status !== 401 && error.status !== 403)
    )
      return false;
    this.accessLost = true;
    this.clearProtected();
    return true;
  }
  private operationError(error: unknown): void {
    if (this.handleAccess(error)) return;
    if (
      error instanceof HttpErrorResponse &&
      (error.status === 409 || error.status === 412)
    ) {
      this.stale = true;
      this.invalidateDraftPreview();
      this.invalidateVersionPreview();
      this.error =
        'The saved source or lifecycle selection changed. Your text is preserved. Refresh the source before trying again.';
    } else if (error instanceof HttpErrorResponse && error.status === 400) {
      this.error =
        'This operation was rejected. Check the plain text, saved revision, independent review and selected version. Your input is preserved.';
    } else
      this.error =
        'The operation could not be confirmed. Your input is preserved. Retry the same action or refresh its source.';
  }
}
